/**
 * ECONOMY BALANCE CONFIG (GAME_SPEC section 26).
 *
 * All numbers that affect game balance live here so the game can be tuned
 * WITHOUT changing engine logic. Engines must read from this config, never
 * hardcode economy numbers.
 *
 * Phase 1 only uses STARTING_CASH / STARTING_REPUTATION / COMPANY_NAME.
 * The rest are defined now so later phases (economy, pricing, demand,
 * staff, upgrades) plug in without restructuring.
 */
export const economyConfig = {
  // --- Player start (spec section 5) ---
  STARTING_CASH: 3_000_000, // THB
  STARTING_REPUTATION: 50, // 0-100
  DEFAULT_COMPANY_NAME: 'Toon Parking',

  // --- Reputation bounds (spec section 16) ---
  REPUTATION_MIN: 0,
  REPUTATION_MAX: 100,

  /**
   * Reputation model (spec section 16). Customer satisfaction is a weighted
   * blend of factors (weights sum to 1.0); reputation drifts toward it each
   * day so it responds to sustained performance rather than jumping.
   */
  REPUTATION: {
    DRIFT_RATE: 0.25, // per day, fraction of the gap closed (faster feedback)
    WEIGHTS: {
      price: 0.2,
      wait: 0.2,
      cleanliness: 0.15,
      security: 0.15,
      availability: 0.2,
      payment: 0.1,
    },
    // Waiting-time satisfaction thresholds (game-minutes, spec section 15).
    WAIT_GREAT_MIN: 2,
    WAIT_BAD_MIN: 10,
    // Price satisfaction: only overpricing (above market) reduces it.
    PRICE_SAT_SLOPE: 70,
    // Payment convenience from cashier throughput.
    PAY_SAT_BASE: 40,
    PAY_SAT_PER_THROUGHPUT: 40,
    // Smoothing for rolling wait/rejection averages tracked on the lot.
    EMA_ALPHA: 0.05,
  },

  // --- Pricing / demand (spec sections 11, 12) — used from Phase 5 ---
  BASE_HOURLY_PRICE: 40, // THB/hour
  AVERAGE_MARKET_PRICE: 40, // THB/hour reference for the price/demand curve
  DEMAND_MULTIPLIER: 1.0, // global demand tuning knob

  // Player price bounds / step for the pricing panel.
  PRICE_MIN: 0,
  PRICE_MAX: 500,
  PRICE_STEP: 5,

  /**
   * Demand curve shape (spec section 11 formula). Tuned so, at the reference
   * market price, the price modifier is ~1.0 and matches the spec examples:
   *   price 40 -> ~1.00, price 60 -> ~0.75, price 80 -> ~0.50.
   */
  DEMAND: {
    // price modifier = 1 - ((price - market)/market) * PRICE_SLOPE, clamped.
    PRICE_SLOPE: 0.5,
    PRICE_MOD_MIN: 0.05,
    PRICE_MOD_MAX: 1.6,
    // time-of-day multipliers
    TIME_PEAK: 1.0,
    TIME_DAY: 0.4, // daytime, non-peak
    TIME_NIGHT: 0.08, // 22:00–06:00
    // reputation multiplier = REP_MIN + (reputation/100) * REP_RANGE
    // (reputation 50 -> 1.0, 100 -> 1.3, 0 -> 0.7)
    REP_MIN: 0.7,
    REP_RANGE: 0.6,
  },

  // --- Recurring costs (spec sections 13, 30) — used from Phase 4 ---
  BASE_RENT: 80_000, // THB/month per lot (before district multiplier)
  MAINTENANCE_RATE: 0.02, // fraction of asset value per month
  ELECTRICITY_RATE: 6.0, // THB per kWh
  ELECTRICITY_KWH_PER_SPACE_DAY: 1.5, // kWh/day per parking space
  SYSTEM_FEE: 5_000, // THB/month
  LOAN_INTEREST_RATE: 0.08, // annual

  // --- Staff salaries (spec section 14) — used from Phase 6 ---
  STAFF_SALARY: {
    cashier: 18_000,
    security: 20_000,
    cleaner: 15_000,
    manager: 35_000,
    technician: 22_000,
  },

  /**
   * How staff translate into lot performance (spec section 14). All tunable so
   * balance can change without touching StaffEngine logic.
   */
  STAFF_EFFECTS: {
    // Cleanliness: base + cleaners, penalized by lot size (more spaces need
    // more cleaners), amplified by managers.
    CLEAN_BASE: 40,
    CLEAN_PER_CLEANER: 30,
    CLEAN_SIZE_PENALTY_PER_SPACES: 40, // -CLEAN_SIZE_PENALTY per this many spaces
    CLEAN_SIZE_PENALTY: 10,
    // Security: CCTV tiles + guards, amplified by managers.
    SEC_PER_CCTV: 10,
    SEC_PER_GUARD: 25,
    // Traffic efficiency: base + managers.
    EFF_BASE: 70,
    EFF_PER_MANAGER: 10,
    // Managers amplify cleaner/guard effectiveness (capped).
    MANAGER_AMP_PER: 0.15,
    MANAGER_AMP_CAP: 0.6,
    // Payment speed: cashiers (and a cashier booth tile) increase throughput.
    PAY_THROUGHPUT_PER_CASHIER: 0.5,
    PAY_THROUGHPUT_CASHIER_TILE: 0.3,
    // Maintenance: technicians reduce upkeep cost (capped).
    MAINT_REDUCTION_PER_TECH: 0.15,
    MAINT_REDUCTION_CAP: 0.6,
  },

  // --- Construction / upgrades / expansion (spec sections 17, 19) ---
  UPGRADE_COST_MULTIPLIER: 1.0,
  EXPANSION_COST: 1_000_000, // per expansion step (spec section 19)
  EXPANSION_STEP: 10, // tiles added to each dimension per expansion
  MAX_LOT_DIM: 40, // largest grid dimension

  // --- Events (spec section 22) — used from Phase 10 ---
  EVENT_PROBABILITY: 0.05, // per game-day chance of an event

  // --- Company valuation weights + win goals (spec section 38) ---
  VALUATION: {
    monthlyProfitMultiple: 24,
    reputationWeight: 10_000,
    marketShareWeight: 500_000,
  },
  GOAL_COMPANY_VALUE: 100_000_000, // THB
  GOAL_MARKET_SHARE: 0.4, // 40%
} as const;

export type EconomyConfig = typeof economyConfig;
